import invariant from "tiny-invariant";

import { useBookingModalContext } from "../contexts/BookingModal";
import { useOpenGamesContext } from "../contexts/OpenGames";
import { useAppSelector } from "../../../hooks/reduxHooks";
import { bookGame, bookPlaceholder } from "../../../lib/api";
import { IGame, IUmpire } from "../../../types/api";
import { useUmpireInfoModalContext } from "../../../contexts/UmpireInfoModal";

export const useButtonHandlers = () => {
    const { setUmpireInfoModalContent, setShowUmpireInfoModal } =
        useUmpireInfoModalContext();
    const {
        setBookingModalContent,
        setShowBookingModal,
        setBookingStatus,
        setBookingStatusMessage,
    } = useBookingModalContext();
    const { setGames } = useOpenGamesContext();

    const { user } = useAppSelector(state => state.auth);
    invariant(user);

    const handleBookingClick = (game: IGame, location: IUmpire["location"]) => {
        setBookingModalContent({ game, location });
        setShowBookingModal(true);
    };

    const handleInfoClick = (umpire: IUmpire) => {
        setUmpireInfoModalContent(umpire);
        setShowUmpireInfoModal(true);
    };

    const handleBookingConfirmClick = async (
        game: IGame,
        location: IUmpire["location"],
    ) => {
        const isGameBooking = game.id !== null;
        setBookingStatus("pending");

        const action = isGameBooking
            ? bookGame(game.id, location, user)
            : bookPlaceholder(
                  game.league,
                  game.date,
                  game.endDate,
                  game.ageLevel.id,
                  game.fieldId,
                  game.diamond,
                  location,
                  user,
              );
        await action
            .then(response => {
                const { game } = response;
                if (isGameBooking) {
                    setGames(prev =>
                        prev.map(g => (g.id === game.id ? game : g)),
                    );
                } else {
                    setGames(prev => {
                        return prev.map(g => {
                            if (
                                g.league === game.league &&
                                g.date === game.date &&
                                g.endDate === game.endDate &&
                                g.ageLevel.id === game.ageLevel.id &&
                                g.fieldId === game.fieldId &&
                                g.diamond === game.diamond
                            ) {
                                return game;
                            } else {
                                return g;
                            }
                        });
                    });
                }
                setBookingStatus("success");
                setBookingStatusMessage(
                    "Success! This game has been added to your schedule.",
                );
            })
            .catch(e => {
                setBookingStatus("failed");
                setBookingStatusMessage(`This game could not be booked: ${e}`);
            });
    };

    return { handleBookingClick, handleInfoClick, handleBookingConfirmClick };
};
